import {
  Button,
  FormProvider,
  MainLayout,
  NotificationCard,
  OneColumnStack,
  RHFTextField,
  Section,
  Link,
} from "@arp/react-ndbx";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Fade, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import Page from "../../components/Page";
import RetrieveOtpVerificationModal from "../../components/pageLevel/otpVerificationModal/RetrieveOtpVerificationModal";
import {
  Application,
  TRACKIFY_EVENT,
  onlineRetrieveContent,
  URLS,
} from "../../config/const/enums";
import {
  onlineRetrieveDisclaimer,
  onlineRetrievePageDisclaimer,
} from "../../config/disclaimers/disclaimers";
import { pageName } from "../../config/pageInfo/pageNames";
import { onlineRetrieveQuoteSchema } from "../../schema/retrieveQuoteSchema";
import IdentityHelper from "../../utilities/IdentityHelper";
import {
  getQuoteInformationFromOkta,
  oktaAuthenticate,
  oktaProceed,
} from "../../utilities/oktaVerificationUtil";
import { pushEvent, trackifyPageChange } from "@allianz/tracking";

function RetrieveQuoteOnline() {
  const navigate = useNavigate();
  const [openOtpVerification, setOpenOtpVerification] = useState(false);
  const [otpSendSuccess, setOtpSendSuccess] = useState(false);
  const [quoteNumberOtpModal, setQuoteNumberOtpModal] = useState();
  const [mobileNumberModal, setMobileNumberModal] = useState();
  const [quoteRetrieveError, setQuoteRetrieveError] = useState(false);
  const [retrieveLoading, setRetrieveLoading] = useState(false);
  const location = useLocation();
  // Parse the query parameters from the location's search property
  const searchParams = new URLSearchParams(location.search);
  // Retrieve the value of the 'quoteNumber' parameter
  const quoteNumber = searchParams.get("quoteNumber");

  const initialData = {
    quoteNumber: quoteNumber || "",
  };

  const handleOtpVerificationClose = (event, reason) => {
    if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
      setOpenOtpVerification(false);
      setOtpSendSuccess(false);
      setQuoteRetrieveError(false);
    }
  };
  const verifyOtpNumberModal = () => {
    setOtpSendSuccess(true);
  };

  const methods = useForm({
    defaultValues: initialData,
    resolver: yupResolver(onlineRetrieveQuoteSchema),
    mode: "all",
    reValidateMode: "all",
  });

  const {
    handleSubmit,
    formState: { isValid },
  } = methods;

  const onSubmit = async (data) => {
    try {
      setRetrieveLoading(true);
      setQuoteNumberOtpModal(data.quoteNumber);
      IdentityHelper.clearStorage(Application.ONLINE);
      IdentityHelper.initOktaConfig(Application.ONLINE);
      let oktaClient = IdentityHelper.getOktaAuthClient();
      await oktaAuthenticate(oktaClient);
      const oktaProceedResponse = await oktaProceed(
        oktaClient,
        data.quoteNumber
      );
      const oktaQuoteDetails = getQuoteInformationFromOkta(oktaProceedResponse);
      if (oktaQuoteDetails.quoteValid) {
        setQuoteRetrieveError(false);
        setMobileNumberModal(oktaQuoteDetails.mobileNumber);
        setOpenOtpVerification(true);
        trackifyPageChange(TRACKIFY_EVENT.RETRIEVE_SEND_OTP_MODAL);
      } else {
        setQuoteRetrieveError(true);
      }
    } catch (ex) {
      console.error(ex);
    }
    setRetrieveLoading(false);
  };

  useEffect(() => {
    trackifyPageChange(TRACKIFY_EVENT.RETRIEVE_QUOTE_ONLINE);
    pushEvent("purchasePath", Application.ONLINE, true);
  }, []);

  return (
    <>
      <Page
        title="Retrieve quote online"
        identifier={pageName.CC_PROCESSING_LANDING_PAGE}
      >
        <MainLayout
          maxWidth="lg"
          sx={{
            mt: 8,
            mb: 5,
            outline: "0px solid purple",
            minHeight: "100vh",
          }}
        >
          <Page
            title="Retrieve quote online"
            identifier="retrieve-quote-online"
          >
            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
              <Section md={7} mt={8}>
                <Section textAlign="left" mb={3}>
                  <Typography variant="h3-bold">
                    {onlineRetrieveContent.RETRIEVE_LIFE_PPLICATION}
                  </Typography>
                </Section>
                <Section textAlign="left" mb={4}>
                  <Typography variant="p2">
                    {onlineRetrieveContent.ENTER_DETAILS}
                  </Typography>
                </Section>
                <Section mb={6}>
                  <RHFTextField
                    name="quoteNumber"
                    defaultValue={quoteNumber || ""}
                    className="subvariant-text-input"
                    variant="standard"
                    label="Application/quote reference number"
                    inputProps={{ style: { textTransform: "uppercase" } }}
                    type="text"
                  />
                </Section>
                {quoteRetrieveError && (
                  <Section mb={7}>
                    <Fade in={true} timeout={500}>
                      <Box>
                        <NotificationCard
                          cardType="error"
                          title=""
                          message={
                            <>
                              {onlineRetrieveContent.APPLICATION_NOT_FOUND}{" "}
                              <Link href={URLS.CONTACT_US_PHONE_URL}>
                                {URLS.CONTACT_US_PHONE_NUMBER}
                              </Link>.
                            </>
                          }
                        />
                      </Box>
                    </Fade>
                  </Section>
                )}
                <OneColumnStack flexShrink={1} mb={6}>
                  <Button
                    id="cc-processing-continue"
                    ariaLabel="continue"
                    variant="contained"
                    color="primary"
                    size="medium"
                    disabled={!isValid || retrieveLoading}
                    type="submit"
                  >
                    {onlineRetrieveContent.RETRIEVE_APPLICATION_BUTTON}
                  </Button>
                </OneColumnStack>
                <Section textAlign="left" mb={10}>
                  <Typography variant="p2">
                    {onlineRetrievePageDisclaimer.DUTY_DISCLOSURE}
                  </Typography>
                </Section>
              </Section>
              {quoteNumberOtpModal && mobileNumberModal && (
                <RetrieveOtpVerificationModal
                  openOtpVerification={openOtpVerification}
                  handleOtpVerificationClose={handleOtpVerificationClose}
                  verifyOtpNumberModal={verifyOtpNumberModal}
                  otpSendSuccess={otpSendSuccess}
                  quoteRefNumber={quoteNumberOtpModal}
                  mobileNumber={mobileNumberModal}
                />
              )}
            </FormProvider>
          </Page>
        </MainLayout>
      </Page>
    </>
  );
}

export default RetrieveQuoteOnline;
